import { Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import ReportsBase from './ReportsBase';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import React from 'react';
import axios from 'axios';
import { useEffect, useState } from 'react';
import '../App.css';
import { DownloadForOffline } from '@mui/icons-material';
import * as XLSX from 'xlsx';

export default function ReportsHistorical() {
    const [allFinancials, setAllFinancials] = useState([]);
    const [peerGroups, setPeerGroups] = useState([]);
    const [selectedPeerGroup, setSelectedPeerGroup] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const reportCount = 8;

    // Fetch available peer groups for the user
    useEffect(() => {
        axios({
            url: process.env.REACT_APP_API_URL + 'PeerGroup',
            method: "Get",
        }).then((res) => {
            setPeerGroups(res.data);
            // Set first peer group as default if available
            if (res.data.length > 0) {
                setSelectedPeerGroup(res.data[0].peerGroupId);
            }
        }).catch((err) => {
            console.log("Error fetching peer groups:", err);
        });
    }, []);

    // Fetch historical report data when peer group changes
    useEffect(() => {
        if (!selectedPeerGroup) return;
        
        setIsLoading(true);
        axios({
            url: process.env.REACT_APP_API_URL + `HistoricalReport?peerGroupId=${selectedPeerGroup}`,
            method: "Get",
        }).then((res) => {
            setIsLoading(false);
            // Group by year first, then by division
            const organizedFinancials = [];
            
            // Convert years to numbers for proper numerical sorting
            const numericYears = res.data.map(f => ({
                ...f,
                yearNum: parseInt(f.fiscalEnd || 0, 10)
            }));
            
            // Get all unique years and sort them numerically (oldest to newest)
            const uniqueYears = [...new Set(numericYears.map(f => f.yearNum))];
            uniqueYears.sort((a, b) => a - b); // Numeric sort for years
            
            console.log("Years in sorted order:", uniqueYears);
            
            uniqueYears.forEach(yearNum => {
                // Get all data for this year
                const yearData = numericYears.filter(f => f.yearNum === yearNum);
                
                // Get unique divisions for this year and sort them
                const uniqueDivisions = [...new Set(yearData.map(f => f.division))].sort();
                
                console.log(`Year ${yearNum} divisions:`, uniqueDivisions);
                
                // Process each division within this year
                uniqueDivisions.forEach(division => {
                    const divisionData = yearData
                        .filter(f => f.division === division)
                        .sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
                    
                    // Add all items for this year/division combination
                    organizedFinancials.push(...divisionData);
                });
            });
            
            setAllFinancials(organizedFinancials);
            console.log("Final organized financials:", organizedFinancials.map(f => `${f.division} - ${f.year}`));
        }).catch((err) => {
            setIsLoading(false);
            console.log("Error fetching historical report:", err);
        });
    }, [selectedPeerGroup]);

    const handlePeerGroupChange = (event) => {
        setSelectedPeerGroup(event.target.value);
    };

    // Download PDF for all reports
    const dlReport = () => {
        if (allFinancials.length === 0) {
            return 0;
        }

        let numberOfGroups = Math.floor(allFinancials.length / reportCount);
        const remainder = allFinancials.length % reportCount;
        numberOfGroups += (remainder > 0 ? 1 : 0);

        const doc = new jsPDF({ orientation: 'l', format: 'letter' });
        
        // Add title
        doc.setFontSize(16);
        doc.text("Historical Financial Reports", doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });
        
        let currentDivision = '';
        let currentYear = '';
        
        for (let i = 0; i < numberOfGroups; i++) {
            // Check if this table represents a new division or year
            const tableId = `tblHistory${i}`;
            const table = document.getElementById(tableId);
            
            if (table) {
                const firstRow = table.querySelector('tr');
                const firstCell = firstRow?.querySelector('td, th');
                const divisionYearText = firstCell?.textContent || '';
                
                // Try to extract division and year
                const match = divisionYearText.match(/([^-]+)\s*-\s*(\d{4})/);
                if (match) {
                    const division = match[1].trim();
                    const year = match[2].trim();
                    
                    // If new division or year, add a page break
                    if (i > 0 && (division !== currentDivision || year !== currentYear)) {
                        doc.addPage();
                        doc.setFontSize(14);
                        doc.text(`${division} - ${year}`, doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });
                    }
                    
                    currentDivision = division;
                    currentYear = year;
                }
            }
            
            // Add the table to PDF
            if (i > 0) {
                doc.autoTable({
                    html: `#${tableId}`,
                    useCss: true,
                    theme: 'plain',
                    startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : 25,
                    pageBreak: 'avoid'
                });
            } else {
                doc.autoTable({ 
                    html: `#${tableId}`, 
                    useCss: true, 
                    theme: 'plain',
                    startY: 25
                });
            }
        }

        doc.save('historical_report.pdf');
        const uri = doc.output('datauristring');
        const newTab = window.open();
        newTab?.document.write(`<iframe width='100%' height='100%' src='${uri}'></iframe>`);
    };

    const dlExcel = () => {
        // Copy and adapt Excel download functionality from Reports.js
        // ...
    };

    return (
        <>
            <div className="container" style={{ 
                maxWidth: '100%',
                overflowX: 'auto'  // Adds horizontal scroll if needed
            }}>
                <div style={{
                    padding: '20px',
                    marginBottom: '20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '20px',
                    flexWrap: 'wrap'
                }}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <h2>Historical Reports</h2>
                        
                        <FormControl size="small" style={{ minWidth: 200 }}>
                            <InputLabel>Peer Group</InputLabel>
                            <Select
                                value={selectedPeerGroup}
                                label="Peer Group"
                                onChange={handlePeerGroupChange}
                                disabled={peerGroups.length === 0}
                            >
                                {peerGroups.map((group) => (
                                    <MenuItem key={group.peerGroupId} value={group.peerGroupId}>
                                        {group.peerName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        
                        <Button 
                            variant="outlined" 
                            onClick={dlReport} 
                            size='small' 
                            startIcon={<DownloadForOffline />}
                            sx={{ mr: 1 }}
                            disabled={allFinancials.length === 0 || isLoading}
                        >
                            Download PDF
                        </Button>
                        <Button 
                            variant="outlined" 
                            onClick={dlExcel} 
                            size='small' 
                            startIcon={<DownloadForOffline />}
                            disabled={allFinancials.length === 0 || isLoading}
                        >
                            Download Excel
                        </Button>
                    </div>
                </div>

                <div style={{ 
                    width: '100%',
                    overflowX: 'auto'  // Adds horizontal scroll if needed
                }}>
                    {isLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>Loading historical data...</div>
                    ) : allFinancials.length > 0 ? (
                        /* All reports in a single view */
                        <ReportsBase 
                            financials={allFinancials} 
                            reportCount={reportCount} 
                            htmlBaseId="tblHistory" 
                            showYear={true}  // Add this prop to ReportsBase to display year in headers
                        />
                    ) : selectedPeerGroup ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>No historical data available for the selected peer group.</div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px' }}>Please select a peer group to view historical reports.</div>
                    )}
                </div>
                
                {/* Hidden section for PDF generation */}
                <div style={{ display: 'none' }}>
                    <ReportsBase 
                        financials={allFinancials} 
                        reportCount={reportCount} 
                        htmlBaseId="tblHistory" 
                        forPrint={true}
                        showYear={true}
                    />
                </div>
            </div>
        </>
    );
}